/**
 * Thread types — mirrored from @padcanvas/shared/threadTypes.
 * Shared-ui cannot import from @padcanvas/shared directly.
 */

/** `help_request` is the old name for a student doubt; the server reads it as
 *  `student_teacher` and hands threads back under the new name. */
export type ThreadType = 'student_teacher' | 'help_request' | 'app_feedback';
export type ThreadStatus = 'open' | 'in_progress' | 'replied' | 'resolved' | 'wont_fix' | 'closed';
export type ThreadObjectType = 'question' | 'submission' | 'hint' | 'session_report' | 'survey' | 'assignment_attempt';

/** What a `student_teacher` thread is about. See docs/FeatureDesigns/student-teacher-threads/. */
export type StudentTeacherKind = 'question_doubt' | 'solution_doubt' | 'solution_feedback' | 'assignment_feedback';

/** The object each kind hangs off. Must match @padcanvas/shared STUDENT_TEACHER_KINDS. */
export const STUDENT_TEACHER_KIND_OBJECT: Record<StudentTeacherKind, ThreadObjectType> = {
    question_doubt: 'question',
    solution_doubt: 'submission',
    solution_feedback: 'submission',
    assignment_feedback: 'assignment_attempt',
};

export function isFeedbackKind(kind: StudentTeacherKind | undefined): boolean {
    return kind === 'solution_feedback' || kind === 'assignment_feedback';
}

export type AppFeedbackKind = 'bug' | 'suggestion' | 'flag' | 'survey';

/** Sentinel ownerId for all `app_feedback` threads. Must match @padcanvas/shared. */
export const ADMIN_FEEDBACK_OWNER_ID = 'system:admin-feedback';

export interface ThreadMessage {
    id: string;
    senderId: string;
    senderName: string;
    senderRole: 'student' | 'teacher' | 'admin';
    text: string;
    attachmentUrls?: string[];
    createdAt: string;
    editedAt?: string;
    /** Admin reply only: also shown to the submitter as an in-app pop-up. */
    showAsPopup?: boolean;
}

export interface HelpRequestContext {
    questionId: string;
    questionTitle?: string;
    subject: string;
    inputMode: 'draw' | 'text' | 'photo';
    solutionImageUrl?: string;
    typedText?: string;
    selectedOption?: string;
}

export interface SolutionFeedbackContext {
    questionId: string;
    questionTitle?: string;
    subject?: string;
}

export interface AssignmentFeedbackContext {
    questionSetId: string;
    assignmentTitle?: string;
    actId?: string;
}

export interface AppFeedbackContext {
    feedbackKind: AppFeedbackKind;
    userName: string;
    userRole: 'student' | 'teacher' | 'admin';
    userEmail?: string;
    appContext?: {
        url: string;
        userAgent: string;
    };
}

export interface Thread {
    id: string;
    partitionKey: string;
    type: ThreadType;
    /** `student_teacher` only. */
    kind?: StudentTeacherKind;
    status: ThreadStatus;
    ownerId: string;
    assignedTo: string;
    objectType?: ThreadObjectType;
    objectId?: string;
    title: string;
    description: string;
    screenshotUrls?: string[];
    editedAt?: string;
    context?: Record<string, unknown>;
    messages: ThreadMessage[];
    seenBy: Record<string, string>;
    createdAt: string;
    createdBy: string;
    createdOnBehalfBy?: string;
    updatedBy: string;
}
